import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Paper } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import axiosInstance from '../util/axios_instance';
import { useNavigate } from 'react-router-dom';

function EarlyAccess() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        email: ''
    })

    const handleEmailChange = (e) => {
        setFormData({
            email: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        let response;
        e.preventDefault();
        try {
            response = await axiosInstance.post(`/user/early_access`, formData, {
                withCredentials: false
            })
        } catch (validationErrors) {
            console.log(validationErrors);
        } finally {
            if (response && response.status === 200) {
                navigate('/early_access_confirmation');
            }
        }
    }

    const faqs = [
        {
            question: 'What happens after I sign up?',
            answer: 'Once you sign up, you\'ll be added to our early access list. We\'ll send you an email confirmation shortly after. As soon as a spot opens up, you\'ll receive an exclusive invitation to join the platform.'
        },
        {
            question: 'When will the full version be released?',
            answer: 'We are targeting an early 2026 launch for the full version of Capsuleme. Early access users will be the first to know and will have the opportunity to transition their accounts seamlessly.'
        },
        {
            question: 'Is early access free?',
            answer: 'Yes, participating in our early access program is completely free. It\'s our way of saying thank you for helping us test and improve the platform before the official launch.'
        }
    ];

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff', fontFamily: 'Inter, sans-serif', display: 'flex', flexDirection: 'column' }}>
            <Helmet>
                <title>Early Access - Capsuleme</title>
                <meta name='description' content='Join our early access program and be the first to experience our platform' />
            </Helmet>

            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'center',
                    px: { xs: 2, sm: 4, md: 8, lg: 12, xl: 20 },
                    py: 2.5
                }}>
                <Box sx={{ width: '100%', maxWidth: '960px', display: 'flex', flexDirection: 'column', flex: 1 }}>

                    <Box component="main" sx={{ flexGrow: 1 }}>
                        <Box sx={{ py: { xs: 8, sm: 12 } }}>
                            {/* Early Access Section */}
                            <Box
                                sx={{
                                    mx: 'auto',
                                    maxWidth: '640px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 4,
                                    px: 2,
                                    textAlign: 'center'
                                }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            color: '#111827',
                                            fontSize: { xs: '2rem', sm: '3rem' },
                                            fontWeight: 900,
                                            letterSpacing: '-0.033em',
                                            lineHeight: 1.2
                                        }}>
                                        Get Early Access
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#4b5563',
                                            fontSize: '1.125rem',
                                            fontWeight: 400
                                        }}>
                                        Be the first to experience the future of digital journaling. Sign up now to get exclusive access to new features, provide feedback, and help shape the development of our platform.
                                    </Typography>
                                </Box>

                                {/* Form */}
                                <Box
                                    component="form"
                                    onSubmit={handleSubmit}
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        alignItems: 'center',
                                        gap: 2
                                    }}>
                                    <TextField
                                        type="email"
                                        name="email"
                                        placeholder="Enter your email"
                                        value={formData.email}
                                        onChange={handleEmailChange}
                                        required
                                        variant="outlined"
                                        sx={{
                                            width: { xs: '100%', sm: 'flex' },
                                            flex: { xs: 0, sm: 1 },
                                            '& .MuiOutlinedInput-root': {
                                                height: 48,
                                                fontSize: '1rem',
                                                '& fieldset': {
                                                    borderColor: '#d1d5db'
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#2574eb'
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#2574eb'
                                                }
                                            }
                                        }}
                                    />
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        sx={{
                                            width: { xs: '100%', sm: 'auto' },
                                            minWidth: '84px',
                                            height: 48,
                                            px: 3,
                                            bgcolor: '#2574eb',
                                            color: 'white',
                                            textTransform: 'none',
                                            fontSize: '1rem',
                                            fontWeight: 'bold',
                                            letterSpacing: '0.015em',
                                            whiteSpace: 'nowrap',
                                            '&:hover': { bgcolor: '#1e5fc4' }
                                        }}>
                                        Request Early Access
                                    </Button>
                                </Box>
                            </Box>

                            {/* FAQ Section */}
                            <Box
                                sx={{
                                    mx: 'auto',
                                    maxWidth: '896px',
                                    px: 2,
                                    pt: { xs: 8, sm: 12 }
                                }}>
                                <Box sx={{ textAlign: 'center', mb: 4 }}>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontSize: '1.875rem',
                                            fontWeight: 'bold',
                                            color: '#111827',
                                            letterSpacing: '-0.015em'
                                        }}>
                                        Frequently Asked Questions
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {faqs.map((faq, index) => (
                                        <Paper
                                            key={index}
                                            elevation={0}
                                            sx={{
                                                p: 0,
                                                border: 'none',
                                                bgcolor: 'transparent'
                                            }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '1.125rem',
                                                    color: '#111827',
                                                    mb: 1
                                                }}>
                                                {faq.question}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#4b5563',
                                                    fontSize: '1rem',
                                                    lineHeight: 1.6
                                                }}>
                                                {faq.answer}
                                            </Typography>
                                        </Paper>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default EarlyAccess;